import { Box, InputAdornment, TextField } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import DeleteIcon from "../components/IconComp/deleteIcon";
import { ColorPallet } from "../utils/colorPallet";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import AllTable from "../components/commontable/table";
import { getSupplierData } from "../features/supplier/supplierSlice";
import { SupplierToolbar } from "../components/inventory/supplier/supplier-tool-bar";
import { EditSupplierToolbar } from "../components/inventory/supplier/supplier-edit-toolbar";
import LoadingSpinner from "../components/common/Loading";
import SearchIcon from "../components/IconComp/searchIcon";
import { SupplierHeader } from "../utils/constants";
import usePermissions from "../hooks/usePermissions";
import { PaginationDTO } from "../dtos/manufacturer";
interface IProps {
  supplier: any;
}

const Supplier = ({ supplier }: IProps) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const [searchQuery, setSearchQuery] = useState(""); // Search query state
  const [error, setError] = useState<string | null>(null); // Add error state
  //pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(""); // Debounce
  // Use useAppSelector to access Redux state
  const rowData = useAppSelector((state) => state?.supplier);
  const pharmacyId = localStorage?.getItem("pharmacyId");
  const isPermissions = usePermissions();
  const Account_Type = parseInt(
    localStorage.getItem("pharmacy_account_type") || "0",
    10
  );
  const searchInputRef = useRef<HTMLInputElement | null>(null); // Ref for search input

  // Debounce effect for search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // Adjust debounce delay as needed
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const getSupplier = useCallback(async () => {
    const dto: PaginationDTO = {
      page: page + 1, // API pages are 1-indexed
      limit: rowsPerPage,
      search: debouncedSearchQuery,
    };
    try {
      setIsLoading(true); // Start loading
      setError(rowData?.message);
      const res = await dispatch(
        getSupplierData({ pharmacy_id: pharmacyId, dto })
      );
      if (res.payload) {
        setTotalCount(res?.payload?.pagination?.totalItems || 0); // Ensure number type
      }
    } catch (error) {
      console.error("Error fetching manufacturer data:", error);
    } finally {
      setIsLoading(false); // Stop loading
      setError("");
      // Only focus if there's an active search query
      if (debouncedSearchQuery) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
    }
  }, [dispatch, pharmacyId, page, rowsPerPage, debouncedSearchQuery]); // Add dependencies as needed

  useEffect(() => {
    getSupplier();
  }, [getSupplier]);

  // Preserve cursor position while typing
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { selectionStart, selectionEnd } = e.target; // Save cursor position
    setSearchQuery(e.target.value);
    setPage(0);
    // Restore cursor position after state update
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.setSelectionRange(selectionStart, selectionEnd);
      }
    }, 0);
  };

  // Filter supplier data based on search query
  const filteredRow = rowData?.supplierDetails
    ?.filter(
      (item: any) =>
        item?.supplier_company_name
          ?.toLowerCase()
          ?.startsWith(searchQuery?.toLowerCase()) ||
        item?.contact_person
          ?.toLowerCase()
          ?.startsWith(searchQuery?.toLowerCase()) ||
        item?.phone_number
          ?.toLowerCase()
          ?.startsWith(searchQuery?.toLowerCase()) ||
        item?.email?.toLowerCase()?.startsWith(searchQuery?.toLowerCase())
    )
    ?.sort((a: any, b: any) =>
      a?.supplier_company_name
        ?.toLowerCase()
        ?.localeCompare(b?.supplier_company_name?.toLowerCase())
    )
    ?.map((item: any) => ({
      "Supplier Name/Company": item?.supplier_company_name,
      "Contact Person": item?.contact_person,
      "Phone#": item?.phone_number,
      Email: item?.email ? item?.email : "-",
      Actions: (
        <>
          {/* <DeleteIcon /> */}
          {(Account_Type === 0 ||
            (isPermissions?.edit && Account_Type === 1)) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                padding: "5px 0px",
              }}
            >
              <EditSupplierToolbar item={item} getSupplier={getSupplier} />
            </Box>
          )}
        </>
      ),
    }));

  const hasAccess =
    Account_Type === 0 || (isPermissions?.view && Account_Type === 1);

  const filteredRowData = hasAccess ? filteredRow : [];

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
  };

  return (
    <>
      {/* <Box sx={{ p: 3, fontSize: "24px", fontWeight: "bold" }}>
        List of Supplier Information
      </Box> */}

      <Box
        sx={{
          margin: 1,
          backgroundColor: ColorPallet?.bgcolor,
          // marginTop: "10px",
          // padding: "10px",
          borderRadius: "5px",
          minHeight: {
            xs: "auto",
            sm: "80vh",
          },
          my: "1.2%",
        }}
      >
        <Box
          sx={{
            px: 1,
            paddingBottom: 1,
            display: "flex",
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ mt: { xs: 1, sm: -1 } }}>
            <TextField
              inputRef={searchInputRef} // Assign ref to maintain focus
              placeholder="Search..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { sx: "200px", sm: "350px" } }}
            />
          </Box>
          <Box>
            <SupplierToolbar getSupplier={getSupplier} />
          </Box>
        </Box>

        {/* Conditional rendering of message or table */}

        <Box sx={{ px: 1, paddingBottom: 1, width: "100%" }}>
          {error ? (
            <>
              <AllTable
                headerData={SupplierHeader}
                rowData={filteredRowData}
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                hasAccess={hasAccess}
              />
              <Box sx={{ textAlign: "center", mt: 4, p: 4, fontSize: "14px" }}>
                {error}
              </Box>
            </>
          ) : (
            <>
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "75vh", // Set height to full viewport height
                  }}
                >
                  <LoadingSpinner />
                </Box>
              ) : (
                <AllTable
                  headerData={SupplierHeader}
                  rowData={filteredRowData}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  totalCount={totalCount}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  hasAccess={hasAccess}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Supplier;
